import { h } from "preact";
import { SwatchCheck, CaretDown } from "../../helpers/icons";

/**
 * A single selectable thread-color swatch (radio), showing the color's image
 * when one is set, falling back to its hex value.
 */
function ColorSwatch({ color, selected, onSelect }) {
  const url = color.image?.image?.url;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={color.label}
      title={color.label}
      className={`monogram-color-selector__swatch ${
        selected ? "monogram-color-selector__swatch--selected" : ""
      }`}
      onClick={() => onSelect(color.id)}
    >
      <span
        className="monogram-color-selector__circle"
        style={url ? undefined : { background: color.color }}
      >
        {url && (
          <img
            src={window.getSizedImageUrl(url, "80x")}
            alt=""
            loading="lazy"
          />
        )}
        <span className="monogram-color-selector__check">
          <SwatchCheck />
        </span>
      </span>
    </button>
  );
}

/**
 * The "Thread Color" step (step 3). Renders the matching / style / additional
 * color groups (see groupMonogramColors), each only when it has at least one
 * color, followed by a config-driven "Looking for more colors?" disclosure built
 * on the global <conditional-details> element.
 */
export default function MonogramColorSelector({
  groups,
  selectedId,
  onSelect,
  styleName,
  strings = {},
  moreColorsCopy,
}) {
  const { matching = [], style = [], additional = [] } = groups || {};

  const renderSwatch = (color) => (
    <ColorSwatch
      key={color.id}
      color={color}
      selected={color.id === selectedId}
      onSelect={onSelect}
    />
  );

  const styleLabel = (strings.style_colors || "{style} Style Colors")
    .replace("{style}", styleName || "")
    .trim();

  return (
    <div className="monogram-color-selector">
      {matching.length > 0 && (
        <div className="monogram-color-selector__group monogram-color-selector__group--matching">
          <p className="monogram-color-selector__heading body">
            {strings.matching_thread_color}
          </p>
          <div
            className="monogram-color-selector__row"
            role="radiogroup"
            aria-label={strings.matching_thread_color}
          >
            {matching.map(renderSwatch)}
          </div>
        </div>
      )}

      {style.length > 0 && (
        <div className="monogram-color-selector__group">
          <p className="monogram-color-selector__heading body">{styleLabel}</p>
          <div
            className="monogram-color-selector__row"
            role="radiogroup"
            aria-label={styleLabel}
          >
            {style.map(renderSwatch)}
          </div>
        </div>
      )}

      {additional.length > 0 && (
        <div className="monogram-color-selector__group">
          <p className="monogram-color-selector__heading body">
            {strings.additional_color_options}
          </p>
          <div
            className="monogram-color-selector__grid"
            role="radiogroup"
            aria-label={strings.additional_color_options}
          >
            {additional.map(renderSwatch)}
          </div>
        </div>
      )}

      {moreColorsCopy && (
        <conditional-details className="monogram-color-selector__more">
          <details className="monogram-color-selector__more-details">
            <summary className="monogram-color-selector__more-summary">
              <span className="monogram-color-selector__more-label caption-1">
                {strings.more_colors}
              </span>
              <span className="monogram-color-selector__more-icon">
                <CaretDown />
              </span>
            </summary>
            <div className="monogram-color-selector__more-content" data-content>
              <div
                className="monogram-color-selector__more-copy rte--simple"
                dangerouslySetInnerHTML={{ __html: moreColorsCopy }}
              />
            </div>
          </details>
        </conditional-details>
      )}
    </div>
  );
}
